import {useRouter} from 'next/router';
import {motion} from 'framer-motion';
import {Box, Heading, Text, BoxProps, Flex} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {useRegionSettings} from '@eksab/hooks/useRegionSettings';
import {getLanguage} from '@eksab/util';

import {BadgesIcon} from './BadgesIcon';
import {CoinsIcon} from './CoinsIcon';
import {MarketIcon} from './MarketIcon';

import {Tabs} from '../';

const bgColor: Record<string, string> = {
  '/store/market': '#74a3ab',
  '/store/badges': '#5AC57B',
  '/store/coins': '#AFAA7F',
  '/store/coins/packages': '#AFAA7F',
};

interface HeaderProps extends BoxProps {
  showsTabs?: boolean;
}

export function Header({showsTabs = true, ...props}: HeaderProps) {
  const router = useRouter();
  const t = useTranslate();
  const {data: regionSettings} = useRegionSettings();

  const backgroundPositionX = getLanguage(router.locale) === 'en' ? 'right' : 'left';
  const routes = [
    {
      title: 'store.coins',
      path: '/store/coins',
      icon: CoinsIcon,
      enabled: !!regionSettings?.settings?.coin_packages_enabled,
    },
    {
      title: 'store.badges',
      path: '/store/badges',
      icon: BadgesIcon,
      enabled: !!regionSettings?.settings?.badge_packages_enabled,
    },
    {title: 'store.market', path: '/store/market', icon: MarketIcon, enabled: true},
  ] as const;

  return (
    <Flex pos="relative" flex="1" maxW="full" {...props}>
      <Box
        pos="absolute"
        as={motion.div}
        animate={{
          background: `linear-gradient(180deg, ${bgColor[router.pathname]} 0%, #7A7A7A 100%)`,
        }}
        w="100%"
        h="100%"
        borderRadius="3xl"
        zIndex={-1}
      />

      <Flex
        flex="1"
        maxW="full"
        flexDir="column"
        justify="center"
        pt={[6, null, null, 16]}
        pb={[4, null, null, 14]}
        px={['4', null, null, '12']}
        _after={{
          content: '""',
          pos: 'absolute',
          inset: 0,
          top: [4, null, 0],
          insetEnd: [2, 4, 8],
          zIndex: -1,
          bg: 'url(/images/store-header-bg.png) no-repeat',
          backgroundPositionX,
          backgroundPositionY: ['top', null, 'center'],
          bgSize: ['6rem', null, 'auto 70%'],
        }}
      >
        <Heading
          lineHeight="1.5"
          fontWeight="extrabold"
          color="white"
          fontSize={['24', null, null, '34']}
          mb={['unset', null, null, '4']}
        >
          {t('store.header')}
        </Heading>

        <Text
          fontSize={['14', null, null, '20']}
          fontWeight="600"
          color="white"
          mb={['6', null, null, '4']}
          w="4xl"
          maxW="80%"
        >
          {t(`store.header.${router.pathname.split('/')[2] as 'badges' | 'market' | 'coins'}`)}
        </Text>

        {showsTabs && (
          <Tabs border="none" py={[1, null, 1.5, 2]} px={[2, 3, 4]} gap="1.5">
            {routes.map(
              (route) =>
                route.enabled && (
                  <Tabs.Item
                    key={route.path}
                    title={route.title}
                    isActive={route.path === router.pathname}
                    icon={<route.icon active={route.path === router.pathname} />}
                    textAlign={['center', null, null, 'start']}
                    color="#8f8f8f"
                    fontSize="sm"
                    fontWeight="500"
                    py="1.5"
                    px={[3, 4]}
                    _active={{bgColor: 'black', boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.2)'}}
                    onClick={() => router.push(route.path)}
                  />
                ),
            )}
          </Tabs>
        )}
      </Flex>
    </Flex>
  );
}
